import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  formatDate,
  formatTime,
  formatShortDate,
  isDifferentDates,
} from "../../extends/NumberFormat";
import PopoverButton from "../PopoverButton/PopoverButton";
import { Typography, Grid, CircularProgress } from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import PopoverNotes from "../PopoverNotes/PopoverNotes";

const styles = (theme) => ({
  border: {
    border: "1px solid #F6F6F9",
    borderRadius: 10,
    background: "#F6F6F9",
    padding: 13,
    marginBottom: 20,
  },
  bold: {
    fontWeight: "bold",
  },
  dateTimeline: {
    lineHeight: "1.1",
    fontSize: 14,
  },
  smallText: {
    fontSize: 13,
    wordWrap: "break-word",
    fontStyle: "italic",
  },
  flex: {
    display: "flex",
    wordWrap: "break-word",
  },
  spacing: {
    paddingBottom: theme.spacing(1),
  },
  centerSpiner: {
    margin: "auto",
    textAlign: "center",
  },
  icon: {
    cursor: "pointer",
    height: 20,
    marginRight: 5,
    width: 20,
  },
  break: {
    justifyContent: "flex-end",
    [theme.breakpoints.down("sm")]: {
      order: 1,
      justifyContent: "flex-start",
      marginTop: 10,
    },
  },
  fullWidth: {
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
    },
  },
});

const EventDetails = (props) => {
  const { classes, eventState, pastEvent } = props;
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
    if (eventState && eventState.length !== 0) {
      Object.values(eventState).map((element) => {
        if (element.comments) {
          element.display = true;
          if (element.notes && element.notes === "<div>&nbsp;</div>") {
            element.display = false;
          }
        }
        return element;
      });
      setLoaded(true);
    }
  }, [eventState]);

  const cropNotes = (notes) => {
    let showButton = false;
    let cutNotes = "";
    if (notes && notes.length > 60) {
      cutNotes = notes.substring(0, 60) + "...";
      showButton = true;
    }

    return (
      <Grid item xs={5} className={classes.fullWidth}>
        <Typography className={classes.smallText}>
          {showButton ? cutNotes : notes}
          {showButton ? <PopoverNotes notes={notes} /> : null}
        </Typography>
      </Grid>
    );
  };

  const notesRender = () => {
    return eventState.proposalNotes ? (
      <div
        className={classes.shadow}
        dangerouslySetInnerHTML={{
          __html: eventState.proposalNotes.notes,
        }}
      />
    ) : (
      <div>No menu notes provided.</div>
    );
  };

  const venueRender = () => {
    if (eventState.event.venue !== null || eventState.event.venue.name !== "") {
      return (
        <Grid item xs={12} className={classes.flex}>
          <Grid container alignItems="center">
            <Grid item xs={12}>
              <Typography variant="subtitle1">
                <span className={classes.bold}>Venue</span>:{" "}
                {eventState.event.venue.name === "Same As Contact"
                  ? `${eventState.event.venue.address1} ${
                      eventState.event.venue.address2
                    } ${
                      eventState.event.venue.city
                        ? eventState.event.venue.city + ","
                        : null
                    } ${eventState.event.venue.state}  ${
                      eventState.event.venue.postalCode
                    }`
                  : eventState.event.venue.name}
                {eventState.eventDetails.venueRooms.length > 0
                  ? eventState.eventDetails.venueRooms.map((venue, index) => {
                      return <span key={index}> {`, ${venue.name}`} </span>;
                    })
                  : null}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      );
    }
  };

  return eventState && eventState.length !== 0 && loaded ? (
    <Grid container>
      {eventState.preparedFor.display ? (
        <Grid item xs={12}>
          <Grid container className={classes.border}>
            <Grid item xs={12} sm={8} className={classes.flex}>
              <Typography variant="subtitle1" className={classes.spacing}>
                <span className={classes.bold}>Prepared For</span>:{" "}
                {eventState.preparedFor.name}
              </Typography>
            </Grid>
            {!pastEvent ? (
              <Grid
                item
                xs={12}
                sm={4}
                className={[classes.flex, classes.break].join(" ")}
              >
                <PopoverButton type={eventState.preparedFor.newComment} />
              </Grid>
            ) : null}

            <Grid item xs={12} sm={8} className={classes.flex}>
              <Typography variant="subtitle1" className={classes.spacing}>
                <span className={classes.bold}>Invoice #</span>:{" "}
                {eventState.event.invoiceNumber}
              </Typography>
            </Grid>

            {eventState.preparedFor.cell ? (
              <Grid item xs={12} className={classes.flex}>
                <Typography variant="subtitle1" className={classes.spacing}>
                  <span className={classes.bold}>Mobile</span>:{" "}
                  {eventState.preparedFor.cell}
                </Typography>
              </Grid>
            ) : null}

            {eventState.preparedFor.work ? (
              <Grid item xs={12} className={classes.flex}>
                <Typography variant="subtitle1" className={classes.spacing}>
                  <span className={classes.bold}>Work</span>:{" "}
                  {eventState.preparedFor.work}
                </Typography>
              </Grid>
            ) : null}
          </Grid>
        </Grid>
      ) : null}

      {eventState.eventDetails.display ? (
        <Grid item xs={12}>
          <Grid container className={classes.border}>
            <Grid item xs={12} sm={8} className={classes.flex}>
              <Typography variant="subtitle1" className={classes.spacing}>
                <span className={classes.bold}>Event Date:</span>{" "}
                {formatDate(eventState.eventDetails.date)}
              </Typography>
            </Grid>

            {!pastEvent ? (
              <Grid
                item
                xs={12}
                sm={4}
                className={[classes.flex, classes.break].join(" ")}
              >
                <PopoverButton type={eventState.eventDetails.newComment} />
              </Grid>
            ) : null}

            {eventState.eventDetails.startTime !== null ||
            eventState.eventDetails.endTime !== null ? (
              <Grid item xs={12} className={classes.flex}>
                <Typography variant="subtitle1" className={classes.spacing}>
                  <span className={classes.bold}>Event Time</span>:{" "}
                  {isDifferentDates(
                    eventState.eventDetails.startTime,
                    eventState.eventDetails.endTime
                  ) ? (
                    <React.Fragment>
                      {eventState.eventDetails.startTime ? (
                        <React.Fragment>
                          {formatTime(eventState.eventDetails.startTime)}{" "}
                          <span className={classes.smallText}>
                            (
                            {formatShortDate(eventState.eventDetails.startTime)}
                            )
                          </span>
                        </React.Fragment>
                      ) : null}
                      {eventState.eventDetails.endTime ? (
                        <React.Fragment>
                          {" "}
                          -{formatTime(eventState.eventDetails.endTime)}
                          <span className={classes.smallText}>
                            ({formatShortDate(eventState.eventDetails.endTime)})
                          </span>
                        </React.Fragment>
                      ) : null}
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <React.Fragment>
                        {eventState.eventDetails.startTime
                          ? formatTime(eventState.eventDetails.startTime)
                          : null}
                      </React.Fragment>
                      <React.Fragment>
                        {eventState.eventDetails.endTime
                          ? ` - ${formatTime(eventState.eventDetails.endTime)}`
                          : null}
                      </React.Fragment>
                    </React.Fragment>
                  )}
                </Typography>
              </Grid>
            ) : null}

            <Grid item xs={12} className={classes.flex}>
              <Typography variant="subtitle1" className={classes.spacing}>
                <span className={classes.bold}>Guest Count</span>:{" "}
                {eventState.eventDetails.guestCount}
              </Typography>
            </Grid>

            <Grid item xs={12} className={classes.flex}>
              <Typography variant="subtitle1" className={classes.spacing}>
                <span className={classes.bold}>Service Style</span>:{" "}
                {eventState.eventDetails.serviceStyle}
              </Typography>
            </Grid>

            <Grid item xs={12} className={classes.flex}>
              <Typography variant="subtitle1" className={classes.spacing}>
                <span className={classes.bold}>Occasion</span>:{" "}
                {eventState.eventDetails.occasion
                  ? eventState.eventDetails.occasion
                  : null}
              </Typography>
            </Grid>
            {venueRender()}
          </Grid>
        </Grid>
      ) : null}

      {eventState.eventTime.times && eventState.eventTime.display ? (
        <Grid item xs={12}>
          <Grid
            container
            justifyContent="space-between"
            className={classes.border}
          >
            <Grid item xs={12} sm={8} className={classes.flex}>
              <Typography variant="subtitle1" className={classes.spacing}>
                <span className={classes.bold}>Timeline</span>:
              </Typography>
            </Grid>
            {!pastEvent ? (
              <Grid
                item
                xs={12}
                sm={4}
                className={[classes.flex, classes.break].join(" ")}
              >
                <PopoverButton type={eventState.eventTime.newComment} />
              </Grid>
            ) : null}
            <Grid container>
              {eventState.eventTime
                ? eventState.eventTime.times.map((event) => {
                    return (
                      <Grid item xs={12} md={12} key={event.id}>
                        <Grid container className={classes.flex}>
                          <Grid item xs={6} sm={2} md={2}>
                            <Typography
                              variant="subtitle1"
                              className={classes.bold}
                            >
                              {formatTime(event.time)}
                            </Typography>
                            {new Date(event.time).getDate() !==
                            new Date(eventState.eventDetails.date).getDate() ? (
                              <Typography
                                variant="subtitle1"
                                className={classes.dateTimeline}
                              >
                                {formatShortDate(event.time)}{" "}
                              </Typography>
                            ) : null}
                          </Grid>
                          <Grid item xs={6} sm={10} md={10}>
                            <Typography variant="subtitle1">
                              {" "}
                              {event.name}{" "}
                            </Typography>
                            {cropNotes(event.notes)}
                          </Grid>
                        </Grid>
                      </Grid>
                    );
                  })
                : null}
            </Grid>
          </Grid>
        </Grid>
      ) : null}

      {eventState.proposalNotes.display ? (
        <Grid item xs={12}>
          <Grid
            className={classes.border}
            container
            justifyContent="space-between"
          >
            <Grid item xs={12} sm={8} className={classes.flex}>
              <Typography variant="subtitle1" className={classes.spacing}>
                <span className={classes.bold}>Proposal Notes</span>:
              </Typography>
            </Grid>
            {!pastEvent ? (
              <Grid
                item
                xs={12}
                sm={4}
                className={[classes.flex, classes.break].join(" ")}
              >
                <PopoverButton type={eventState.proposalNotes.newComment} />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <Typography variant="subtitle1" className={classes.spacing}>
                {notesRender()}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      ) : null}
    </Grid>
  ) : (
    <CircularProgress className={classes.centerSpiner} />
  );
};

EventDetails.propTypes = {
  classes: PropTypes.object.isRequired,
};
export default withStyles(styles)(EventDetails);
